import { useEffect, useRef } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringSuggestProvider } from 'libs/searchspring/fetchers/suggest'
import { SearchspringTrendingProvider } from 'libs/searchspring/fetchers/trending'
import { SearchPageLink } from 'libs/searchspring/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/searchspring/components/quick-search/QuickSearchResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { calculateProductCount } from 'libs/searchspring/components/collection/filters/ProductCount'

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter(s => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter(s => s.type === 'pages') || []

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useSearchspringContext()
  const { title, hasResults, showNoResults, isCheckingResults, isResultsReady } = useSearchResultState({ query, results, isLoading })
  // const Recommendations = useCallback(withRecommendationProvider(CarouselResults, 'recommend'), [])

  const totalResultsCount = calculateProductCount(results)

  const itemClass = 'w-grid-7/8 lg:w-full max-w-[280px] lg:max-w-grid-1/4 mr-gutter [_&>div]:w-full'
  // const productCardSettings = {
  //   hideQuickAdd: false,
  //   hideSwatches: false,
  //   hidePromolines: false,
  //   hideBadges: false,
  //   hideWishlist: false
  // }
  const carouselSettings = {
    draggable: true,
    freeScroll: false,
    wrapAround: false,
    groupCells: true,
    fade: false,
    adaptiveHeight: false,
    accessibility: true,
    cellAlign: 'left',
    prevNextButtons: false,
    pageDots: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  /**
   * Renders suggestion components based on current search state
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    const config = hasResults ? SEARCH_CONFIG.HAS_RESULTS : SEARCH_CONFIG.NO_RESULTS
    return config.suggestions.map((suggestion, index) => (
      <SearchSuggestions
        key={index}
        title={suggestion.title}
        type={suggestion.type}
        query={query}
        defaultSuggestions={suggestion.default}
        isStatic={suggestion.isStatic}
      />
    ))
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or PopularCategories component
   */
  return (
    <div className="flex flex-col gap-x-gutter overflow-y-scroll overscroll-none bg-brand-white no-scrollbar lg:grid lg:max-h-[75vh] lg:grid-cols-main lg:gap-y-1.5 lg:px-margin">
      {/* Main Content Section */}
      <div className="flex h-fit w-full shrink-0 flex-col gap-y-1.25 overflow-x-hidden lg:px-margin py-1.5 lg:col-[3/-1] lg:row-[1/2] lg:w-full-margin-half lg:py-2.5 lg:pl-2">
        <div className={clsx('flex w-full items-center justify-between gap-x-gutter')}>
          {!hasResults && query && !isLoading && showNoResults && (
            <div className="flex w-full flex-col gap-y-1 pb-1.5 lg:pb-1 px-margin lg:px-0">
              <h4 className="h4">Sorry, we couldn't find what you were looking for...</h4>
              <span className="paragraph-m w-full">
                Check your spelling, try another search or browse our popular products and collections below.
              </span>
            </div>
          )}
          {!hasResults && !query && !isLoading && <h4 className="h4 px-margin lg:px-0 lg:pb-1">{title}</h4>}

          {hasResults && !redirect && (
            <div className="flex w-full flex-row items-center justify-between px-margin lg:px-0">
              <h4 className="h4">Search Results</h4>
              <SearchPageLink
                query={query}
                className="btn shrink-0 !font-normal btn-fill btn-primary btn-s lg-max:!hidden"
              >
                View {totalResultsCount} Results
              </SearchPageLink>
            </div>
          )}
        </div>

        <div className="w-full">
          {/* Loading Overlay */}
          <div
            className={clsx(
              'overflow-hidden w-full',
              isCheckingResults || isLoading ? '' : 'pointer-events-none h-0 opacity-0'
            )}
          >
            <SkeletonGrid carouselSettings={carouselSettings} itemClass={itemClass} />
          </div>
          <div className={clsx('w-full', hasResults ? '' : 'hidden')}>{isResultsReady && <QuickSearchResults />}</div>
          <div className={clsx('w-full px-margin lg:px-0', !isCheckingResults && !isLoading && (!query || !hasResults) ? '' : 'hidden')}>
            {popularCategories.current && (
              <PopularCategories content={popularCategories.current} carouselSettings={carouselSettings} />
            )}
          </div>
        </div>
      </div>

      {/* Mobile "View results" button */}
      {hasResults && !redirect && (
        <div className='sticky bottom-0 w-full p-1 lg:px-0 lg:hidden bg-brand-white z-10'>
          <SearchPageLink query={query} className="btn w-full btn-fill btn-primary btn-m btn-bold">
            View {totalResultsCount} Results
          </SearchPageLink>
        </div>
      )}

      {/* Suggestions Section */}
      <div className="flex h-fit w-full shrink-0 flex-col gap-y-1.75 bg-white px-margin py-1.5 lg:col-[1/3] lg:row-[1/2] lg:-ml-margin lg:h-full lg:w-full-margin-half lg:py-2.5 lg:pr-1.75">
        {renderSuggestions()}
      </div>
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content, carouselSettings }) => {
  return (
    <>
      <x-carousel
        className="flex w-full h-fit"
        carousel-settings={JSON.stringify(carouselSettings)}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </>
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {Object} props.carouselSettings - Settings for the carousel
 * @param {string} props.itemClass - CSS class for carousel items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ carouselSettings, itemClass }) => {
  return (
    <x-carousel
      className="flex w-full h-fit"
      carousel-settings={JSON.stringify(carouselSettings)}
    >
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
    </x-carousel>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'pages')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, isStatic = true }) => {
  return (
    <div className="flex flex-col gap-y-0.75 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full lg:after:w-[68%] after:h-px after:bg-neutral-medium pb-1.625 last:after:hidden last:pb-0">
      <h4 className="h4">{title}</h4>
      {isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : (
        query ? (
        <SearchspringSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </SearchspringSuggestProvider>
        ) : (
        <SearchspringTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </SearchspringTrendingProvider>
        )
      )}
    </div>
  )
}
